import { Component, Input, Output, EventEmitter } from '@angular/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ISegmentRule } from '../../../../types/contactModule';
import { countries } from '../../../../../utils/countriesList';
import {
  ATTRIBUTE_TEXT,
  GENDER_OPTIONS,
  OPERATOR_TEXT,
  SEGMENT_ATTRIBUTES,
  SegmentAttributeSpec,
  TSegmentOperator,
  attributeSpec,
  dataTypeFor,
} from '../segment-utilities';

export type { ISegmentAttribute } from '../segment-utilities';

/**
 * One condition: a contact field, a comparison and a value. The row owns
 * no state; every change goes back to the conditions step as a new rule.
 */
@Component({
  selector: 'segment-rule-card',
  standalone: true,
  imports: [MatTooltipModule],
  templateUrl: './segment-rule-card.component.html',
  styleUrl: './segment-rule-card.component.css',
})
export class SegmentRuleCardComponent {
  @Input() segmentRule: ISegmentRule = { attributeName: '', rule: '', values: [] };
  @Input() index!: number;
  @Input() last!: boolean;
  @Input() id: any = null;
  /** What is wrong with this row, once the person tried to move on. */
  @Input() error = '';
  @Input() canRemove = true;
  @Output() updateSegment = new EventEmitter<ISegmentRule & { index: number }>();
  @Output() removeSegmentRule = new EventEmitter<number>();

  readonly attributeText = ATTRIBUTE_TEXT;
  readonly operatorText = OPERATOR_TEXT;
  readonly genderOptions = GENDER_OPTIONS;
  readonly countries = countries;

  get spec(): SegmentAttributeSpec | undefined {
    return attributeSpec(this.segmentRule?.attributeName);
  }

  /** The fields on offer, plus this row's own when an older segment uses one no longer offered. */
  get attributes(): SegmentAttributeSpec[] {
    const spec = this.spec;
    return spec && !SEGMENT_ATTRIBUTES.includes(spec) ? [...SEGMENT_ATTRIBUTES, spec] : SEGMENT_ATTRIBUTES;
  }

  get operators(): TSegmentOperator[] {
    return this.spec?.operators ?? [];
  }

  get value(): string {
    return (this.segmentRule?.values?.[0] ?? '').toString();
  }

  get rowId(): string {
    return `seg-rule-${this.id ?? this.index}`;
  }

  get position(): string {
    return `Condition ${this.index + 1}`;
  }

  /** A different field keeps the comparison when it still applies, and clears a value of another kind. */
  setAttribute(label: string) {
    const next = attributeSpec(label);
    const previous = this.spec;
    const operator = next && next.operators.includes(this.segmentRule.rule as TSegmentOperator)
      ? this.segmentRule.rule
      : next?.operators[0] ?? '';
    const keepValue = !!next && !!previous && next.input === previous.input && next.input !== 'country';
    this.emit({
      attributeName: label,
      rule: operator,
      values: [keepValue ? this.value : ''],
      dataType: dataTypeFor(label),
    });
  }

  setOperator(operator: string) {
    this.emit({ rule: operator });
  }

  setValue(value: string) {
    this.emit({ values: [value ?? ''] });
  }

  removeSegment() {
    this.removeSegmentRule.emit(this.index);
  }

  private emit(change: Partial<ISegmentRule>) {
    this.updateSegment.emit({ ...this.segmentRule, ...change, id: this.segmentRule.id ?? this.id, index: this.index });
  }
}
